import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import ContactConsentModal from "../components/ContactConsentModal";
import { getBuyerByHandle } from "../services/buyers";
import { revealContact } from "../services/sellers";
import { subscribeAuth } from "../services/auth";
import { needsConsent, recordConsent } from "../services/consent";
import { SEGMENTS, CONTACT_CHANNELS } from "../services/constants";
import { compactNumber, currencySymbol, isOnline, regionFlag,
  relativeTime } from "../utils/format";
import { buildSellerToBuyerIntent, contactHref, copyIntent } from "../utils/deeplink";

// Buyer profile / requirement detail. Mirrors the storefront shape so
// the visual rhythm is consistent — same contact gate, same disclaimer.
export default function BuyerProfile() {
  const { handle } = useParams();
  const [buyer, setBuyer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [user, setUser] = useState(null);
  const [revealed, setRevealed] = useState({});
  const [revealError, setRevealError] = useState(null);
  const [consentPending, setConsentPending] = useState(null);

  useEffect(() => subscribeAuth(setUser), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      const b = await getBuyerByHandle(handle);
      if (cancelled) return;
      if (!b) { setNotFound(true); setLoading(false); return; }
      setBuyer(b);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [handle]);

  if (loading) return <div className="container" style={{ padding: 80, textAlign: "center" }}>Loading…</div>;
  if (notFound) return (
    <div className="container" style={{ padding: 80, textAlign: "center" }}>
      <h2>No buyer with handle <code>@{handle}</code>.</h2>
      <Link to="/?role=buyer">Back to buyers</Link>
    </div>
  );

  const online = isOnline(buyer.lastActiveAt);
  const symbol = currencySymbol(buyer.currency || "INR");
  const buyerIntent = buildSellerToBuyerIntent(buyer);

  const doReveal = async (channel) => {
    setRevealError(null);
    if (!user || buyer._seed) {
      setRevealError("🔐 Sign in with email to reveal contact details.");
      return;
    }
    try {
      const res = await revealContact(buyer.handle, channel, "buyer");
      setRevealed((prev) => ({ ...prev, [channel]: res.value }));
    } catch (e) {
      console.warn("[pop-market] reveal failed", e);
      setRevealError(e.message || "Contact details aren't available right now.");
    }
  };
  const reveal = async (channel) => {
    if (!user || buyer._seed) { await doReveal(channel); return; }
    if (needsConsent()) { setConsentPending(channel); return; }
    await doReveal(channel);
  };
  const onConsentAgree = async () => {
    recordConsent();
    const ch = consentPending;
    setConsentPending(null);
    if (ch) await doReveal(ch);
  };

  return (
    <div className="container" style={{ paddingBottom: 80 }}>
      <div style={{ display: "flex", alignItems: "flex-start", gap: 18, padding: "24px 0" }}>
        <div style={{
          width: 96, height: 96, borderRadius: 18,
          background: "linear-gradient(135deg, rgba(78,200,227,0.18), var(--bg-elev))",
          border: "1px solid rgba(78,200,227,0.35)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontWeight: 800, fontSize: 42, color: "var(--segment-lumen)",
          flexShrink: 0,
        }}>
          {(buyer.displayName || buyer.handle)[0].toUpperCase()}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h1 style={{ margin: "0 0 6px", fontSize: 26, fontWeight: 800, display: "flex", alignItems: "center", gap: 10 }}>
            {buyer.displayName || buyer.handle}
            <span className="tag" style={{
              background: "rgba(78,200,227,0.15)",
              color: "var(--segment-lumen)",
              border: "1px solid rgba(78,200,227,0.45)",
              fontSize: 9, letterSpacing: 1.5,
            }}>BUYER</span>
          </h1>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 13, color: "var(--text-muted)" }}>
            <span className={`tag ${buyer.game === "BGMI" ? "bgmi" : "pubgm"}`}>{buyer.game}</span>
            <span>{regionFlag(buyer.region, buyer.countryCode)} {buyer.city ? `${buyer.city}, ${buyer.region}` : buyer.region}</span>
            <span>·</span>
            <span>@{buyer.handle}</span>
            {online ? (
              <span style={{ color: "var(--ok)", fontWeight: 700 }}>● Online now</span>
            ) : (
              <span className="dim">Last seen {relativeTime(buyer.lastActiveAt)}</span>
            )}
          </div>
          {buyer.bio && <p style={{ margin: "8px 0 0", color: "var(--text-muted)" }}>{buyer.bio}</p>}
          {buyer.battleContext && (
            <div style={{ marginTop: 8, color: "var(--accent)", fontWeight: 600, fontSize: 13 }}>
              ⚔ {buyer.battleContext}
            </div>
          )}
        </div>
      </div>

      {/* Requirements */}
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", margin: "16px 0 12px" }}>
        Requirements
      </div>
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12 }}>
        {SEGMENTS.map((s) => {
          const need = buyer.needs?.[s.key];
          const has = need && Number(need.quantity) > 0;
          const perK = s.key === "mix" || s.key === "premium";
          return (
            <div key={s.key} className="card" style={{ opacity: has ? 1 : 0.5 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
                <span className={`segment-pill ${s.cssClass}`}>{s.label}</span>
                {need?.urgency && has && (
                  <span style={{ fontSize: 9, fontWeight: 800, letterSpacing: 1, padding: "2px 6px", borderRadius: 4, border: "1px solid var(--accent)", color: "var(--accent)" }}>
                    {String(need.urgency).toUpperCase()}
                  </span>
                )}
              </div>
              {has ? (
                <>
                  <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 4 }}>
                    {compactNumber(need.quantity)}
                    <span className="dim" style={{ fontSize: 11, fontWeight: 500 }}> needed</span>
                  </div>
                  <div style={{ fontSize: 13, color: "var(--text-muted)" }}>
                    Max: <strong style={{ color: "var(--text)" }}>{symbol}{Number(need.maxRate || 0).toFixed(need.maxRate >= 100 ? 0 : 2)}</strong>
                    {perK ? "/k" : ""}
                  </div>
                </>
              ) : (
                <div className="dim">Not needed</div>
              )}
            </div>
          );
        })}
      </div>

      {/* Budget */}
      {buyer.budgetINR && (
        <div className="card" style={{ marginTop: 12 }}>
          <div className="dim" style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1, textTransform: "uppercase" }}>
            Total budget for this run
          </div>
          <div style={{ fontSize: 28, fontWeight: 800, color: "var(--segment-lumen)", marginTop: 4 }}>
            ₹{compactNumber(buyer.budgetINR)}
          </div>
        </div>
      )}

      {/* Payment methods accepted */}
      {buyer.paymentMethods?.length > 0 && (
        <>
          <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", margin: "24px 0 12px" }}>
            Will pay via
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {buyer.paymentMethods.map((p) => <span key={p} className="tag">{p}</span>)}
          </div>
        </>
      )}

      {/* Contact — buttons always visible; click-time inline gates. */}
      <div style={{ fontSize: 12, fontWeight: 800, letterSpacing: 1.2, textTransform: "uppercase", color: "var(--text-muted)", margin: "24px 0 12px" }}>
        Contact this buyer
      </div>
      <div className="card" style={{ display: "flex", flexDirection: "column", gap: 10 }}>
        {CONTACT_CHANNELS
          .filter((c) => (buyer.availableChannels || []).includes(c.key))
          .map((c) => {
            const value = revealed[c.key];
            const href = value ? contactHref(c.key, value, buyerIntent) : undefined;
            const onOpen = () => {
              if (c.key !== "whatsapp") copyIntent(buyerIntent);
            };
            return (
              <div key={c.key} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: "1px solid var(--border)" }}>
                <span style={{ fontWeight: 600 }}>{c.icon} {c.label}</span>
                {value ? (
                  <a style={{ fontWeight: 700, color: "var(--segment-lumen)", fontFamily: "ui-monospace, monospace" }}
                    href={href}
                    onClick={onOpen}
                    target="_blank" rel="noopener noreferrer">
                    {value}
                  </a>
                ) : (
                  <button className="primary" onClick={() => reveal(c.key)}>🔒 Show</button>
                )}
              </div>
            );
          })}
        {(buyer.availableChannels || []).length === 0 && (
          <div className="dim">Buyer hasn't added contact info yet.</div>
        )}
        {revealError && (
          <div style={{
            background: "rgba(232,90,45,0.08)",
            border: "1px solid rgba(232,90,45,0.3)",
            borderRadius: 8,
            padding: "10px 12px",
            color: "var(--text)",
            fontSize: 13,
            marginTop: 8,
            display: "flex",
            alignItems: "center",
            gap: 8,
            flexWrap: "wrap",
          }}>
            <span>{revealError}</span>
            {revealError.includes("Sign in") && (
              <Link to="/signin" style={{ background: "var(--accent)", color: "#1a0f00", fontWeight: 700, padding: "4px 10px", borderRadius: 6, textDecoration: "none", fontSize: 12 }}>
                Sign in →
              </Link>
            )}
          </div>
        )}
      </div>
      <div className="dim" style={{ fontSize: 11, textAlign: "center", marginTop: 10, fontStyle: "italic" }}>
        PlayAware does not verify post-contact transactions or mediate
        disputes. Conduct due diligence before engaging.
      </div>

      <ContactConsentModal
        open={consentPending != null}
        sellerName={buyer.displayName || `@${buyer.handle}`}
        onAgree={onConsentAgree}
        onCancel={() => setConsentPending(null)}
      />
    </div>
  );
}

